import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Table } from "../../components/SystemComponent/table";
import { Badge } from "../../components/SystemComponent/Bagde";
import { Switch } from "../../components/SystemComponent/Switch";
import {
  MdPerson,
  MdEmail,
  MdPhone,
  MdWork,
  MdBusiness,
  MdGroup,
  MdBlock,
  MdCheckCircle,
} from "react-icons/md";
import { createApi } from "../../../core/api";

export const UserList = () => {
  const api = useMemo(() => createApi(), []);
  const { setPageMenus } = useOutletContext<any>();

  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r: any = await api.user.getAll();
      if (r?.ok) {
        const list = r.data?.data ?? r.data ?? [];
        setData(Array.isArray(list) ? list : []);
      }
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    setPageMenus([
      {
        label: "Users",
        items: [{ label: "Users", route: "/dashboard/users" }],
      },
    ]);
    load();
    return () => setPageMenus([]);
  }, [load, setPageMenus]);

  const toggleBlockUser = async (userId: number) => {
    await api.user.setBlockUnblock(userId);
    load();
  };

  const summary = useMemo(() => {
    const total = data.length;
    const blocked = data.filter((x) => x.isDeleted).length;
    const active = total - blocked;

    const roleCounts = data.reduce((acc: any, u: any) => {
      const role = u.role?.name ?? "No Role";
      acc[role] = (acc[role] || 0) + 1;
      return acc;
    }, {});

    return {
      total,
      active,
      blocked,
      roleCounts,
    };
  }, [data]);

  const columns = useMemo<any[]>(
    () => [
      {
        key: "username",
        title: "User",
        minWidth: 320,
        accessor: (u: any) => `${u.firstName} ${u.lastName}`,
        render: (u: any, value: string) => {
          const isBlocked = u.isDeleted;
          const mediaUrl =
            typeof u.profileMedia?.url === "string" ? u.profileMedia.url : null;
          const avatar = u.username?.charAt(0)?.toUpperCase() ?? "?";

          return (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {mediaUrl ? (
                <img
                  src={mediaUrl}
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    objectFit: "cover",
                    opacity: isBlocked ? 0.4 : 1,
                  }}
                />
              ) : (
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    background: "#E5E7EB",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    opacity: isBlocked ? 0.4 : 1,
                  }}
                >
                  {avatar}
                </div>
              )}

              <Badge
                icon={MdPerson}
                label={value}
                color={isBlocked ? "#DC2626" : "#22C55E"}
                bg={isBlocked ? "rgba(220,38,38,.15)" : "rgba(34,197,94,.15)"}
              />
            </div>
          );
        },
      },
      {
        key: "email",
        title: "Email",
        minWidth: 260,
        accessor: (u: any) => u.email,
        render: (u: any, value: string) =>
          value ? (
            <Badge
              icon={MdEmail}
              label={value}
              color="#2563EB"
              bg="rgba(37,99,235,.1)"
            />
          ) : (
            "-"
          ),
      },
      {
        key: "phone",
        title: "Phone",
        minWidth: 200,
        accessor: (u: any) => u.phoneNumber,
        render: (u: any, value: string) =>
          value ? (
            <Badge
              icon={MdPhone}
              label={value}
              color="#0F766E"
              bg="rgba(15,118,110,.1)"
            />
          ) : (
            "-"
          ),
      },
      {
        key: "company",
        title: "Company",
        minWidth: 220,
        accessor: (u: any) => u.companyUser?.company?.companyName,
        render: (u: any, value: string) =>
          value ? (
            <Badge
              icon={MdBusiness}
              label={value}
              color="#0369A1"
              bg="rgba(3,105,161,.12)"
            />
          ) : (
            "-"
          ),
      },
      {
        key: "role",
        title: "Role",
        minWidth: 180,
        accessor: (u: any) => u.role?.name,
        render: (u: any, value: string) =>
          value ? (
            <Badge
              icon={MdWork}
              label={value}
              color="#7C3AED"
              bg="rgba(124,58,237,.12)"
            />
          ) : (
            "-"
          ),
      },
      {
        key: "emailVerify",
        title: "Email Verify",
        type: "boolean",
        minWidth: 160,
        accessor: (u: any) => u.emailVerify,
        render: (u: any, value: boolean) => (
          <Switch value={value} onChange={() => {}} size={32} />
        ),
      },
      {
        key: "isDeleted",
        title: "Block / Unblock",
        type: "boolean",
        minWidth: 160,
        accessor: (u: any) => !u.isDeleted,
        render: (u: any, value: boolean) => (
          <Switch
            value={value}
            onChange={() => toggleBlockUser(u.id)}
            size={32}
          />
        ),
      },
    ],
    [load]
  );

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <div>
          <div style={S.title}>Users</div>
          <div style={S.subTitle}>All system users</div>
        </div>
      </div>

      <div style={S.badgeCard}>
        <div style={S.badgeGrid}>
          <Badge
            icon={MdGroup}
            label="Total Users"
            value={summary.total}
            color="#6366F1"
            bg="rgba(99,102,241,.18)"
          />
          <Badge
            icon={MdCheckCircle}
            label="Active Users"
            value={summary.active}
            color="#16A34A"
            bg="rgba(22,163,74,.18)"
          />
          <Badge
            icon={MdBlock}
            label="Blocked Users"
            value={summary.blocked}
            color="#DC2626"
            bg="rgba(220,38,38,.18)"
          />

          {Object.entries(summary.roleCounts).map(([role, count]) => (
            <Badge
              key={role}
              icon={MdWork}
              label={role}
              value={count as number}
              color="#7C3AED"
              bg="rgba(124,58,237,.18)"
            />
          ))}
        </div>
      </div>

      <Table
        tableKey="users"
        loading={loading}
        columns={columns}
        data={data}
        features={{
          sortable: true,
          filterable: true,
          pagination: true,
          persistence: true,
        }}
      />
    </div>
  );
};

const S: Record<string, React.CSSProperties> = {
  wrap: { position: "absolute", inset: 0, padding: 20, background: "#F8FAFC" },
  header: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  title: { fontSize: 18, fontWeight: 600, color: "#0F172A" },
  subTitle: { fontSize: 13, color: "#64748B" },
  badgeCard: {
    background: "#fff",
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
  },
  badgeGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))",
    gap: 12,
  },
};
